/**
 * RangerStalenessBanner (#5223)
 * -----------------------------
 * "Answers affected by new material" for the investment Diligence tab: how
 * many Sirius AI answers are stale, when the last targeted re-run happened,
 * a Review dialog with one card per re-checked field (what happened to it,
 * previous / new answer, the source that moved it — unchanged fields listed
 * too), "Apply as draft" on a suggestion, and a button to re-run only the
 * stale answers. Re-runs also start on their own when new material is
 * embedded, so the status is polled while the tab is open.
 *
 * Self-contained on purpose: it takes the investment id and an optional
 * display-name lookup rather than reaching into the template store, so it can
 * be rendered — and tested — without one.
 */
import { gql } from "_graphql-types-frontend/gql";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { asFullDateTime } from "../../utils/date";
import CitationDisplay from "./Field/CitationDisplay";

export const RANGER_DRAFT_STATUS = gql(/* GraphQL */ `
  query RangerDraftStatus($investmentId: Int!) {
    rangerDraftStatus(investmentId: $investmentId) {
      investmentId
      lastRunAt
      staleFields {
        ownerType
        ownerId
        fieldKey
        markedAt
        artifacts {
          type
          id
        }
      }
      lastRun {
        runId
        ranAt
        fields {
          ownerType
          ownerId
          fieldKey
          status
          oldValue
          newValue
          triggeredBy {
            type
            id
          }
          citations {
            type
            id
          }
        }
      }
    }
  }
`);

export const RERUN_STALE_RANGER_FIELDS = gql(/* GraphQL */ `
  mutation RerunStaleRangerFields($investmentId: Int!) {
    rerunStaleRangerFields(investmentId: $investmentId) {
      id
    }
  }
`);

/**
 * "Apply as draft" (Nick 2026-09-15 item 6): the suggested answer goes into the
 * person's Draft state through the existing draft path — the server derives the
 * state, so Published and every other state stay untouched.
 */
export const APPLY_RANGER_SUGGESTION_INVESTMENT = gql(/* GraphQL */ `
  mutation ApplyRangerSuggestionToInvestmentDraft($investmentId: Int!, $values: [FieldValueInput!]!) {
    saveInvestmentFieldDrafts(investmentId: $investmentId, values: $values) {
      investmentId
      state
    }
  }
`);

export const APPLY_RANGER_SUGGESTION_FIRM = gql(/* GraphQL */ `
  mutation ApplyRangerSuggestionToFirmDraft($firmId: Int!, $values: [FieldValueInput!]!) {
    saveFirmFieldDrafts(firmId: $firmId, values: $values) {
      firmId
      state
    }
  }
`);

export const RANGER_RERUN_TASK_STATUS = gql(/* GraphQL */ `
  query RangerRerunTaskStatus($id: ID!) {
    asyncTaskStatus(id: $id) {
      id
      endDate
      error
    }
  }
`);

interface RangerStalenessBannerProps {
  investmentId: number;
  /**
   * Offer the "Re-run" action. The banner and the Review dialog are read-only
   * for everyone who can read the investment; re-running writes fields, so it
   * is offered only where the template already offers writes (edit mode).
   */
  canRerun: boolean;
  /** Human label for a field; falls back to the field key. */
  getDisplayName?: (ownerType: string, fieldKey: string) => string | undefined;
  /** Runs after a re-run finishes so the host can refresh its Sirius AI field data. */
  onRerunComplete?: () => unknown;
  /** How often to poll the re-run job; a test seam. */
  pollIntervalMs?: number;
  /**
   * How often to refresh the status while the tab is open — re-runs start on
   * their own when new material is embedded, so the banner must move without
   * a click. 0 disables (tests).
   */
  statusRefreshMs?: number;
}

/**
 * What happened to the field and where to look for it (Nick 2026-09-15 item 3:
 * "does the Sirius AI answer get pushed or not?"). The chip is the short form;
 * the outcome sentence is shown on every row and summarised in the legend.
 */
export const STATUS_LABEL: Record<
  string,
  { label: string; color: "success" | "default" | "warning" | "error"; outcome: string }
> = {
  changed: {
    label: "Changed",
    color: "success",
    outcome: "Sirius AI suggestion updated — open the field's Sirius AI tab to see it.",
  },
  answered: {
    label: "Newly answered",
    color: "success",
    outcome:
      "This question had no answer before — the new material produced one. Open the field's Sirius AI tab to see it.",
  },
  unchanged: {
    label: "Unchanged",
    color: "default",
    outcome: "No change — the re-check produced the same answer.",
  },
  suggested: {
    label: "Suggested — you edited this field",
    color: "warning",
    outcome: "Not written — your edit is kept. Apply the new answer as a draft below if you want it.",
  },
  failed: {
    label: "Not written — still stale",
    color: "error",
    outcome: "Not written — the write failed, so the field stays stale and the next re-run retries it.",
  },
};

const OUTCOME_LEGEND =
  "Newly answered = a question with no answer now has one · Changed = Sirius AI suggestion updated · Unchanged = no change · Suggested = not written, your edit kept (apply as draft) · Not written = still stale";

/** Answers are stored as text or Quill HTML; the diff shows plain text. */
export function valueAsText(value: unknown): string {
  if (value === null || value === undefined) return "—";
  const text = typeof value === "string" ? value : JSON.stringify(value);
  const stripped = text.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  return stripped === "" ? "—" : stripped;
}

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export default function RangerStalenessBanner({
  investmentId,
  canRerun,
  getDisplayName,
  onRerunComplete,
  pollIntervalMs = 3000,
  statusRefreshMs = 30_000,
}: RangerStalenessBannerProps): JSX.Element | null {
  const client = useApolloClient();
  // The re-run poll must die with the component: no query after navigating
  // away, no setState on an unmounted component.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const { data, error, loading, refetch } = useQuery(RANGER_DRAFT_STATUS, {
    variables: { investmentId },
    fetchPolicy: "network-only",
    pollInterval: statusRefreshMs || undefined,
  });
  const [rerun] = useMutation(RERUN_STALE_RANGER_FIELDS);
  const [applyToInvestmentDraft] = useMutation(APPLY_RANGER_SUGGESTION_INVESTMENT);
  const [applyToFirmDraft] = useMutation(APPLY_RANGER_SUGGESTION_FIRM);
  const [open, setOpen] = useState(false);
  const [rerunning, setRerunning] = useState(false);
  const [rerunError, setRerunError] = useState<string | null>(null);
  // Per field: "applying" while the draft write is in flight, "applied" after,
  // or the error text. Keyed by ownerType.fieldKey.
  const [applyState, setApplyState] = useState<Record<string, "applying" | "applied" | string>>({});

  const status = data?.rangerDraftStatus;
  const staleFields = status?.staleFields ?? [];
  const lastRun = status?.lastRun ?? null;

  const label = useCallback(
    (ownerType: string, fieldKey: string) =>
      getDisplayName?.(ownerType, fieldKey) || fieldKey,
    [getDisplayName]
  );

  // Every status the worker records, so the one-line summary sums to the
  // number of fields re-checked.
  const counts = useMemo(() => {
    const out = { answered: 0, changed: 0, unchanged: 0, suggested: 0, failed: 0 };
    for (const field of lastRun?.fields ?? []) {
      if (field.status in out) out[field.status as keyof typeof out] += 1;
    }
    return out;
  }, [lastRun]);
  const countsSummary = `${counts.changed} changed, ${counts.answered} newly answered, ${counts.unchanged} unchanged, ${counts.suggested} suggested, ${counts.failed} not written`;

  const handleRerun = useCallback(async () => {
    setRerunError(null);
    setRerunning(true);
    try {
      const res = await rerun({ variables: { investmentId } });
      const taskId = res.data?.rerunStaleRangerFields.id;
      if (!taskId) throw new Error("The re-run did not start");
      while (mounted.current) {
        const { data: poll } = await client.query({
          query: RANGER_RERUN_TASK_STATUS,
          variables: { id: taskId },
          fetchPolicy: "no-cache",
        });
        const task = poll?.asyncTaskStatus;
        if (task?.error) throw new Error(task.error);
        if (task?.endDate) break;
        await sleep(pollIntervalMs);
      }
      if (!mounted.current) return;
      await refetch();
      await onRerunComplete?.();
      if (mounted.current) setOpen(true);
    } catch (err) {
      if (mounted.current) {
        setRerunError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      if (mounted.current) setRerunning(false);
    }
  }, [client, investmentId, onRerunComplete, pollIntervalMs, refetch, rerun]);

  const handleApplyAsDraft = useCallback(
    async (field: {
      ownerType: string;
      ownerId: number;
      fieldKey: string;
      newValue: unknown;
      citations: { type: string; id: number }[];
    }) => {
      const key = `${field.ownerType}.${field.fieldKey}`;
      setApplyState(prev => ({ ...prev, [key]: "applying" }));
      try {
        const values = [
          {
            fieldKey: field.fieldKey,
            value: field.newValue,
            citations: field.citations.map(c => ({ type: c.type, id: c.id })),
          },
        ];
        if (field.ownerType === "firm") {
          await applyToFirmDraft({ variables: { firmId: field.ownerId, values } });
        } else {
          await applyToInvestmentDraft({
            variables: { investmentId: field.ownerId, values },
          });
        }
        await onRerunComplete?.();
        if (mounted.current) setApplyState(prev => ({ ...prev, [key]: "applied" }));
      } catch (err) {
        if (mounted.current) {
          setApplyState(prev => ({
            ...prev,
            [key]: err instanceof Error ? err.message : String(err),
          }));
        }
      }
    },
    [applyToFirmDraft, applyToInvestmentDraft, onRerunComplete]
  );

  if (loading) return null;
  if (error) {
    return (
      <Alert severity="error" data-testid="ranger-staleness-error">
        Could not load Sirius AI draft status: {error.message}
      </Alert>
    );
  }
  if (!status || (staleFields.length === 0 && !lastRun)) return null;

  const staleCount = staleFields.length;

  return (
    <>
      <Alert
        severity={staleCount > 0 ? "warning" : "info"}
        icon={<AutoAwesomeIcon fontSize="inherit" />}
        data-cy="ranger-staleness-banner"
        data-testid="ranger-staleness-banner"
        sx={{ mb: 2, alignItems: "center" }}
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              color="inherit"
              onClick={() => setOpen(true)}
              data-cy="ranger-staleness-review"
            >
              Review
            </Button>
            {canRerun && staleCount > 0 && (
              <Button
                size="small"
                variant="contained"
                disabled={rerunning}
                onClick={handleRerun}
                startIcon={
                  rerunning ? <CircularProgress size={14} color="inherit" /> : undefined
                }
                data-cy="ranger-staleness-rerun"
              >
                {rerunning
                  ? "Re-running…"
                  : `Re-run ${staleCount} stale answer${staleCount === 1 ? "" : "s"}`}
              </Button>
            )}
          </Stack>
        }
      >
        <Stack spacing={0.25}>
          <Typography variant="body2" data-testid="ranger-staleness-summary">
            {staleCount > 0
              ? `${staleCount} Sirius AI answer${staleCount === 1 ? "" : "s"} affected by new material`
              : "Sirius AI answers are current with the material on record"}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid="ranger-staleness-last-run"
          >
            {lastRun
              ? `Last Sirius AI re-run: ${asFullDateTime(new Date(lastRun.ranAt))} — re-checked ${lastRun.fields.length}: ${countsSummary}`
              : "No Sirius AI re-run yet"}
          </Typography>
          {rerunError && (
            <Typography variant="caption" color="error" data-testid="ranger-staleness-rerun-error">
              {rerunError}
            </Typography>
          )}
        </Stack>
      </Alert>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        fullWidth
        data-testid="ranger-staleness-dialog"
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <AutoAwesomeIcon fontSize="small" />
            <span>Sirius AI answers and new material</span>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" gutterBottom>
            Affected by new material ({staleCount})
          </Typography>
          {staleCount === 0 ? (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Nothing is waiting on a re-run.
            </Typography>
          ) : (
            <Table size="small" data-testid="ranger-stale-table">
              <TableHead>
                <TableRow>
                  <TableCell>Question</TableCell>
                  <TableCell>New material</TableCell>
                  <TableCell>Since</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {staleFields.map(field => (
                  <TableRow key={`${field.ownerType}.${field.fieldKey}`}>
                    <TableCell>{label(field.ownerType, field.fieldKey)}</TableCell>
                    <TableCell>
                      {field.artifacts.length ? (
                        <CitationDisplay citations={field.artifacts} />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Material you do not have access to
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{asFullDateTime(new Date(field.markedAt))}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Typography variant="subtitle2" sx={{ mt: 3 }} gutterBottom>
            {lastRun
              ? `Last re-run ${asFullDateTime(new Date(lastRun.ranAt))} — re-checked ${lastRun.fields.length}: ${countsSummary}`
              : "No re-run yet"}
          </Typography>
          {lastRun && (
            <>
              <Typography
                variant="caption"
                color="text.secondary"
                component="p"
                sx={{ mb: 1 }}
                data-testid="ranger-rerun-legend"
              >
                {OUTCOME_LEGEND}
              </Typography>
              <Stack spacing={1.5} data-testid="ranger-rerun-table">
                {lastRun.fields.map(field => {
                  const chip = STATUS_LABEL[field.status] ?? {
                    label: field.status,
                    color: "default" as const,
                    outcome: "",
                  };
                  const sources = field.citations.length ? field.citations : field.triggeredBy;
                  const applyKey = `${field.ownerType}.${field.fieldKey}`;
                  const apply = applyState[applyKey];
                  return (
                    // One bordered card per field: the question is the card's
                    // heading and the answers sit UNDER it, so a long new answer
                    // never scrolls its question out of view (items 1 + 2).
                    <Paper
                      key={applyKey}
                      variant="outlined"
                      sx={{ p: 1.5 }}
                      data-testid={`ranger-rerun-row-${field.status}`}
                      data-field-key={field.fieldKey}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                          {label(field.ownerType, field.fieldKey)}
                        </Typography>
                        <Chip size="small" label={chip.label} color={chip.color} />
                      </Stack>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                        data-testid="ranger-rerun-outcome"
                      >
                        {chip.outcome}
                      </Typography>
                      <Divider sx={{ my: 1 }} />
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={2}
                        divider={<Divider orientation="vertical" flexItem />}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary" component="p">
                            Previous answer
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ whiteSpace: "pre-wrap" }}
                            color={
                              field.status === "answered" ? "text.secondary" : undefined
                            }
                          >
                            {field.status === "answered"
                              ? "Not previously answered"
                              : valueAsText(field.oldValue)}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary" component="p">
                            New answer
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                            {valueAsText(field.newValue)}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: "0 0 220px", minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary" component="p">
                            Source that moved it
                          </Typography>
                          {sources.length ? <CitationDisplay citations={sources} /> : "—"}
                        </Box>
                      </Stack>
                      {field.status === "suggested" && canRerun && field.newValue !== null && (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={apply === "applying" || apply === "applied"}
                            onClick={() =>
                              handleApplyAsDraft({
                                ownerType: field.ownerType,
                                ownerId: field.ownerId,
                                fieldKey: field.fieldKey,
                                newValue: field.newValue,
                                citations: field.citations,
                              })
                            }
                            data-cy="ranger-apply-as-draft"
                          >
                            {apply === "applied" ? "Applied as draft" : "Apply as draft"}
                          </Button>
                          {apply && apply !== "applying" && apply !== "applied" && (
                            <Typography variant="caption" color="error" data-testid="ranger-apply-error">
                              {apply}
                            </Typography>
                          )}
                        </Stack>
                      )}
                    </Paper>
                  );
                })}
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
